import { Card, Descriptions, Tag } from 'antd'

interface Patient {
  id: string
  email: string
  firstName: string | null
  lastName: string | null
  phone: string | null
  dateOfBirth: string | null
  gender: 'MALE' | 'FEMALE' | 'OTHER' | null
  image: string | null
  status: 'ACTIVE' | 'SUSPENDED' | 'DELETED'
  createdAt: string
  updatedAt: string
  profile: {
    address: string | null
    city: string | null
    postalCode: string | null
    marketingConsent: boolean
    smsConsent: boolean
  } | null
}

const genderLabels: Record<string, string> = {
  MALE: 'Muški',
  FEMALE: 'Ženski',
  OTHER: 'Drugo',
}

const statusConfig: Record<Patient['status'], { color: string; text: string }> = {
  ACTIVE: { color: 'success', text: 'Aktivan' },
  SUSPENDED: { color: 'warning', text: 'Suspendovan' },
  DELETED: { color: 'default', text: 'Obrisan' },
}

function formatDate(dateString: string | null) {
  if (!dateString) return '-'
  return new Date(dateString).toLocaleDateString('sr-Latn-RS', {
    day: 'numeric',
    month: 'long',
    year: 'numeric',
  })
}

interface OverviewTabProps {
  patient: Patient
}

export function OverviewTab({ patient }: OverviewTabProps) {
  const patientName = patient.firstName || patient.lastName
    ? `${patient.firstName || ''} ${patient.lastName || ''}`.trim()
    : patient.email

  return (
    <div>
      {/* Personal Information */}
      <Card title="Lični podaci" style={{ marginBottom: 16 }}>
        <Descriptions column={{ xs: 1, sm: 2, md: 2 }}>
          <Descriptions.Item label="Ime i prezime">{patientName}</Descriptions.Item>
          <Descriptions.Item label="Email">{patient.email}</Descriptions.Item>
          <Descriptions.Item label="Telefon">{patient.phone || '-'}</Descriptions.Item>
          <Descriptions.Item label="Datum rođenja">{formatDate(patient.dateOfBirth)}</Descriptions.Item>
          <Descriptions.Item label="Pol">
            {patient.gender ? genderLabels[patient.gender] : '-'}
          </Descriptions.Item>
        </Descriptions>
      </Card>

      {/* Address */}
      <Card title="Adresa" style={{ marginBottom: 16 }}>
        <Descriptions column={{ xs: 1, sm: 2, md: 3 }}>
          <Descriptions.Item label="Adresa">{patient.profile?.address || '-'}</Descriptions.Item>
          <Descriptions.Item label="Grad">{patient.profile?.city || '-'}</Descriptions.Item>
          <Descriptions.Item label="Poštanski broj">{patient.profile?.postalCode || '-'}</Descriptions.Item>
        </Descriptions>
      </Card>

      {/* Consents */}
      <Card title="Saglasnosti" style={{ marginBottom: 16 }}>
        <Descriptions column={{ xs: 1, sm: 2 }}>
          <Descriptions.Item label="Marketing">
            {patient.profile?.marketingConsent ? (
              <Tag color="success">Da</Tag>
            ) : (
              <Tag>Ne</Tag>
            )}
          </Descriptions.Item>
          <Descriptions.Item label="SMS obaveštenja">
            {patient.profile?.smsConsent ? (
              <Tag color="success">Da</Tag>
            ) : (
              <Tag>Ne</Tag>
            )}
          </Descriptions.Item>
        </Descriptions>
      </Card>

      {/* System Data */}
      <Card title="Sistemski podaci">
        <Descriptions column={{ xs: 1, sm: 2, md: 3 }}>
          <Descriptions.Item label="Status">
            <Tag color={statusConfig[patient.status].color}>
              {statusConfig[patient.status].text}
            </Tag>
          </Descriptions.Item>
          <Descriptions.Item label="Datum registracije">{formatDate(patient.createdAt)}</Descriptions.Item>
          <Descriptions.Item label="Poslednja izmena">{formatDate(patient.updatedAt)}</Descriptions.Item>
        </Descriptions>
      </Card>
    </div>
  )
}
